// src/components/VariantPickerSheet.jsx
//
// Bottom sheet выбора варианта товара при тапе на товар с несколькими вариантами.
// Показывает каждый вариант: фото, имя (атрибуты), цена, остаток.
//
// Используется в:
//   - ShopCashier (для розничных продаж)
//   - ShopWholesaleSale (для оптовых отгрузок)

import { useTranslation } from 'react-i18next';
import { formatMoney } from '../data/currencies';
import Icon from './Icon';

export default function VariantPickerSheet({ product, currency, onPick, onClose }) {
  const { t } = useTranslation();
  const variants = (product.variants || []).filter(v => v.active !== false);
  const productPhoto = product.photo
    || (Array.isArray(product.photos) && product.photos[0])
    || null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 200,
        display: 'flex', alignItems: 'flex-end',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: '100%',
          background: 'var(--bg)',
          borderRadius: '16px 16px 0 0',
          padding: 20,
          maxHeight: '80vh',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {product.name}
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg-muted)' }}>
            <Icon name="x" size={18} />
          </button>
        </div>

        <p style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12 }}>
          {t('variants.pickVariant') || 'Выбери вариант'}
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, overflowY: 'auto' }}>
          {variants.map(v => {
            const variantPhoto = (Array.isArray(v.photos) && v.photos[0]) || productPhoto;
            const attrLine = v.attributeValues
              ? Object.entries(v.attributeValues).map(([k, val]) => val).filter(Boolean).join(' / ')
              : '';
            const stock = Number(v.stock) || 0;
            const lowStock = stock <= 0;
            return (
              <button
                key={v.id}
                onClick={() => onPick(v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: 12,
                  background: 'var(--bg-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-sm)',
                  cursor: 'pointer',
                  textAlign: 'left',
                  opacity: lowStock ? 0.55 : 1,
                }}
              >
                {variantPhoto ? (
                  <img
                    src={variantPhoto}
                    alt=""
                    style={{
                      width: 48, height: 48,
                      borderRadius: 'var(--r-xs)',
                      objectFit: 'cover',
                      flexShrink: 0,
                    }}
                  />
                ) : (
                  <div style={{
                    width: 48, height: 48,
                    borderRadius: 'var(--r-xs)',
                    background: 'var(--bg-subtle)',
                    flexShrink: 0,
                  }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 14, fontWeight: 600 }}>
                    {attrLine || v.name || t('variants.defaultVariant')}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--fg-faint)', fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(v.salePrice || 0, currency)} · {t('products.stock')}: {stock}
                    {v.sku && <> · {v.sku}</>}
                  </p>
                </div>
                <Icon name="chevronRight" size={16} color="var(--fg-faint)" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
